import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ResponsiveScatterPlot } from '@nivo/scatterplot'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  return useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])
}

function DenaliScatterPlot ({
  reportId,
  baseQuery,
  xAccessor,
  xAxisFormat,
  yAccessor,
  yAxisFormat,
  valueFormat,
  height,
  groupAccessor,
  groupLabel,
  ...chartProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(() => {
    return {
      ...baseQuery,
      membershipFilter,
      filters: {
        ...(filter || {}),
        ...(baseQuery?.filters || {})
      },
      reportId
    }
  }, [reportId, baseQuery, filter, membershipFilter])

  const { data: report, isLoading } = useFirmReportData(query)
  const chartData = useMemo(() => {
    if (!report) return []

    if (!groupAccessor) {
      return [
        {
          id: groupLabel,
          data: report.data.map(d => ({
            x: d[xAccessor],
            y: d[yAccessor]
          }))
        }
      ]
    }

    const groupsDict = report.data.reduce((acc, item) => {
      acc[item[groupAccessor]] = item[groupAccessor]
      return acc
    }, {})
    const groups = Object.values(groupsDict)
    return groups.map(group => ({
      id: group,
      data: report.data.filter(d => d[groupAccessor] === group).map(d => ({
        x: Math.abs(d[xAccessor]),
        y: d[yAccessor]
      }))
    }))
  }, [report, groupAccessor, groupLabel, xAccessor, yAccessor])
  const valueFormatter = useFormatters(valueFormat)
  const xAxisFormatter = useFormatters(xAxisFormat)
  const yAxisFormatter = useFormatters(yAxisFormat)
  const axisBottom = useMemo(() => {
    return {
      ...(chartProps.axisBottom || {}),
      format: xAxisFormatter
    }
  }, [chartProps.axisBottom, xAxisFormatter])
  const axisLeft = useMemo(() => {
    return {
      ...(chartProps.axisLeft || {}),
      format: yAxisFormatter
    }
  }, [chartProps.axisLeft, yAxisFormatter])

  if (isLoading) return null

  return (
    <div style={{ height }}>
      <ResponsiveScatterPlot
        theme={{
          text: {
            fontFamily: 'var(--font-sans) !important',
            fontSize: '12px',
            color: 'var(--text-color-main)'
          }
        }}
        data={chartData}
        valueFormat={valueFormatter}
        {...chartProps}
        axisLeft={axisLeft}
        axisBottom={axisBottom}
      />
    </div>
  )
}

DenaliScatterPlot.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  xAccessor: PropTypes.string,
  xAxisFormat: PropTypes.string,
  yAccessor: PropTypes.string,
  yAxisFormat: PropTypes.string,
  valueFormat: PropTypes.string,
  groupAccessor: PropTypes.string,
  groupLabel: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

DenaliScatterPlot.defaultProps = {
  valueFormat: 'marketValue',
  xAxisFormat: 'number',
  yAxisFormat: 'number',
  height: 300
}

export default DenaliScatterPlot
